import React, { useState } from 'react';
import { Filter, ArrowRight, Phone, Mail, MapPin, User } from 'lucide-react';

const RealEstateCustomerService = () => {
  const [selectedTicket, setSelectedTicket] = useState({
    id: '#TKT10823',
    customer: 'Sarah Martinez',
    email: 'sarah.m@email.com',
    phone: '+1 (555) 789-4561',
    location: 'Miami, FL',
    status: 'Open',
    segment: 'Buyer',
    submittedOn: 'March 15, 2024 - 10:41 AM',
    channel: 'Phone',
    subject: 'Property Viewing Request',
    description: 'I\'m interested in viewing the Beachfront Condo property listed at $850,000. I\'d like to schedule a viewing this week. Are there any available time slots? Also, can you provide more details about HOA fees and parking?',
    avatar: 'https://i.pravatar.cc/150?img=1'
  });

  const tickets = [
    { id: '#T10245', customer: 'Michael R.', issue: 'Inspection Delay', orderId: '#PROP-847', status: 'In Progress', assignTo: 'Agent James B.', created: '2:17 pm', avatar: 'https://i.pravatar.cc/150?img=33' },
    { id: '#T10246', customer: 'Emma W.', issue: 'Documentation Issue', orderId: '#PROP-848', status: 'Open', assignTo: 'Agent Sarah K.', created: '1:45 pm', avatar: 'https://i.pravatar.cc/150?img=5' },
    { id: '#T10247', customer: 'David T.', issue: 'Financing Question', orderId: '#PROP-849', status: 'Resolved', assignTo: 'Agent Alex M.', created: '12:30 pm', avatar: 'https://i.pravatar.cc/150?img=12' },
    { id: '#T10248', customer: 'Lisa A.', issue: 'Property Details', orderId: '#PROP-850', status: 'Open', assignTo: 'Agent Emily T.', created: '11:20 am', avatar: 'https://i.pravatar.cc/150?img=10' },
    { id: '#T10249', customer: 'Robert K.', issue: 'Contract Review', orderId: '#PROP-851', status: 'In Progress', assignTo: 'Agent James B.', created: '10:15 am', avatar: 'https://i.pravatar.cc/150?img=68' }
  ];

  const resolvedTickets = [
    { id: '#T10240', issue: 'Viewing Scheduled', status: 'Resolved' },
    { id: '#T10241', issue: 'Price Negotiation', status: 'Resolved' },
    { id: '#T10242', issue: 'Lease Agreement', status: 'Resolved' }
  ];

  const urgentTickets = [
    { id: '#T10250', issue: 'Offer Deadline' },
    { id: '#T10251', issue: 'Closing Issue' },
    { id: '#T10252', issue: 'Title Problem' }
  ];

  return (
    <div className="min-h-screen bg-black text-white p-6 overflow-y-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Customer Service</h1>
        <p className="text-gray-400 text-sm mt-1">Manage customer tickets and support requests</p>
      </div>

      {/* Top KPI Cards */}
      <div className="grid grid-cols-3 gap-6 mb-6">
        {/* Open Tickets */}
        <div className="bg-black border border-white/40 rounded-2xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
          </div>
          <div className="text-4xl font-bold mb-2">142</div>
          <div className="text-sm text-gray-400 mb-1">In Last Month</div>
          <div className="text-sm text-green-400">↑ +25%</div>
        </div>

        {/* Pending Tickets */}
        <div className="bg-black border border-white/40 rounded-2xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <div className="text-4xl font-bold mb-2">64</div>
          <div className="text-sm text-gray-400 mb-1">In Last Month</div>
          <div className="text-sm text-green-400">↑ +25%</div>
        </div>

        {/* Resolved Today */}
        <div className="bg-black border border-white/40 rounded-2xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <div className="text-4xl font-bold mb-2">88</div>
          <div className="text-sm text-gray-400 mb-1">In Last Month</div>
          <div className="text-sm text-green-400">↑ +25%</div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Tickets */}
        <div className="col-span-8 space-y-6">
          {/* Tickets Table */}
          <div className="bg-black border border-white/40 rounded-2xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Tickets</h2>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-400">Total 248</span>
                <button className="flex items-center gap-2 px-4 py-2 bg-black border border-white/60 hover:border-white hover:shadow-[0_0_10px_rgba(255,255,255,0.3)] rounded-lg transition-all">
                  <Filter size={16} />
                  <span className="text-sm font-medium">Filter</span>
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">TICKET ID</th>
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">CUSTOMER</th>
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">ISSUE TYPE</th>
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">ORDER ID</th>
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">STATUS</th>
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">ASSIGN TO</th>
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">CREATED</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket, idx) => (
                    <tr key={idx} className="border-b border-gray-800 hover:bg-gray-900/30 transition-all cursor-pointer">
                      <td className="py-4 text-sm font-medium">{ticket.id}</td>
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <img src={ticket.avatar} alt={ticket.customer} className="w-8 h-8 rounded-full object-cover" />
                          <span className="text-sm">{ticket.customer}</span>
                        </div>
                      </td>
                      <td className="py-4 text-sm">{ticket.issue}</td>
                      <td className="py-4 text-sm text-gray-400">{ticket.orderId}</td>
                      <td className="py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          ticket.status === 'In Progress' ? 'bg-yellow-500/20 text-yellow-400' :
                          ticket.status === 'Open' ? 'bg-cyan-500/20 text-cyan-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td className="py-4 text-sm">{ticket.assignTo}</td>
                      <td className="py-4 text-sm text-gray-400">{ticket.created}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Resolved Tickets & Urgent */}
          <div className="grid grid-cols-2 gap-6">
            {/* Resolved Tickets */}
            <div className="bg-black border border-white/40 rounded-2xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Resolved Tickets</h3>
                <span className="text-sm text-gray-400">Total 142</span>
              </div>

              <div className="space-y-3 mb-4">
                {resolvedTickets.map((ticket, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 hover:bg-gray-900/30 rounded-lg transition-all">
                    <div>
                      <div className="text-sm font-medium">{ticket.id}</div>
                      <div className="text-xs text-gray-400">{ticket.issue}</div>
                    </div>
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                      {ticket.status}
                    </span>
                  </div>
                ))}
              </div>

              <button className="flex items-center justify-center gap-2 w-full py-2 bg-black border border-white/60 hover:border-white hover:shadow-[0_0_10px_rgba(255,255,255,0.3)] rounded-lg transition-all">
                <span className="text-sm font-medium">See More</span>
                <ArrowRight size={16} />
              </button>
            </div>

            {/* Urgent */}
            <div className="bg-black border border-white/40 rounded-2xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Urgent</h3>
                <span className="text-sm text-gray-400">Total 24</span>
              </div>

              <div className="space-y-3 mb-4">
                {urgentTickets.map((ticket, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 hover:bg-gray-900/30 rounded-lg transition-all">
                    <div>
                      <div className="text-sm font-medium">{ticket.id}</div>
                      <div className="text-xs text-gray-400">{ticket.issue}</div>
                    </div>
                    <button className="px-3 py-1 bg-black border border-white/60 hover:border-white hover:shadow-[0_0_10px_rgba(255,255,255,0.3)] rounded text-xs font-medium transition-all">
                      Solve Now
                    </button>
                  </div>
                ))}
              </div>

              <button className="flex items-center justify-center gap-2 w-full py-2 bg-black border border-white/60 hover:border-white hover:shadow-[0_0_10px_rgba(255,255,255,0.3)] rounded-lg transition-all">
                <span className="text-sm font-medium">See More</span>
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Customer Profile */}
        <div className="col-span-4">
          <div className="bg-black border border-white/40 rounded-2xl p-6 hover:border-white/60 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300 sticky top-6">
            {/* Customer Info */}
            <div className="text-center mb-6">
              <img src={selectedTicket.avatar} alt={selectedTicket.customer} className="w-20 h-20 rounded-full mx-auto mb-4 object-cover" />
              <h3 className="text-xl font-bold mb-1">{selectedTicket.customer}</h3>
              <p className="text-sm text-gray-400 mb-2">{selectedTicket.email}</p>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-400 inline-block">
                {selectedTicket.status}
              </span>
            </div>

            {/* Contact Details */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-3 text-sm">
                <Mail size={16} className="text-gray-500" />
                <span className="text-gray-300">sarah@gmail.com</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Phone size={16} className="text-gray-500" />
                <span className="text-gray-300">{selectedTicket.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <MapPin size={16} className="text-gray-500" />
                <span className="text-gray-300">{selectedTicket.location}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <User size={16} className="text-gray-500" />
                <span className="text-gray-300">{selectedTicket.segment}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mb-6">
              <button className="flex-1 py-2 bg-black border border-white/60 hover:border-white hover:shadow-[0_0_10px_rgba(255,255,255,0.3)] rounded-lg text-sm font-medium transition-all">
                Issues
              </button>
              <button className="flex-1 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-all">
                Info
              </button>
            </div>

            {/* Ticket Details */}
            <div className="space-y-4">
              <div>
                <div className="text-xs text-gray-400 mb-1">Ticket ID</div>
                <div className="text-sm font-medium">{selectedTicket.id}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">Submitted On</div>
                <div className="text-sm">{selectedTicket.submittedOn}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">Channel</div>
                <div className="text-sm">{selectedTicket.channel}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">Subject</div>
                <div className="text-sm font-medium">{selectedTicket.subject}</div>
              </div>
              <div>
                <div className="text-xs text-gray-400 mb-1">Description</div>
                <div className="text-sm text-gray-300 leading-relaxed">{selectedTicket.description}</div>
              </div>
            </div>

            <button className="w-full py-3 bg-black border border-white/60 hover:border-white hover:shadow-[0_0_10px_rgba(255,255,255,0.3)] rounded-lg font-medium mt-6 transition-all">
              Reply Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealEstateCustomerService;
